/* ============================================================
   STAFF POS — /pos
   Touch-first counter / till ordering screen. Owner directive
   Feb 2026: replace the "Take Order" tab inside the Bar Panel
   (which was the customer QR menu shoehorned into the till) with
   a dedicated POS optimised for staff speed.

   Stack: same /api/menu, /api/tables, /api/orders plumbing as
   the customer flow — interface only.

   Access: till_supervisor / manager / director. Bartenders stay
   on the stripped prep-only /bar.

   Layout (1080p+ landscape):
      ┌─ Header ─────────────────────────────────────────────┐
      │ MAJNU POS · [Table / Counter toggle] · [Table pick]  │
      │ Name · Sign out                                      │
      ├──────────────────────────────────┬───────────────────┤
      │ [drinks][food][shisha][specials] │  Cart (running)   │
      │ [sub-chips: cocktails / whisky…] │  Item rows + qty  │
      │ Search                           │  Subtotal         │
      │ ┌──┐┌──┐┌──┐┌──┐┌──┐ tiles       │  Discount pills   │
      │ │  ││  ││  ││  ││  │             │  Total            │
      │ └──┘└──┘└──┘└──┘└──┘             │  [EFTPOS][Cash]   │
      │                                  │  [Tab]            │
      └──────────────────────────────────┴───────────────────┘

   Modifier sheet (Sheet from /components/ui/sheet): tap a tile
   with variants OR head_options OR addon_groups → opens. Big
   buttons, qty stepper, Add → cart row.
   ============================================================ */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Minus,
  X,
  CreditCard,
  Banknote,
  Receipt,
  LogOut,
  ShoppingBag,
  Store,
  Coffee,
  Utensils,
  Flame,
  Gamepad2,
  Loader2,
  Trash2,
  ArrowLeft,
  Users,
  RefreshCw,
  CheckCircle2,
  ChevronRight,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import CustomMixDialog from "../components/CustomMixDialog";
import TableFloorView from "../components/TableFloorView";
import SettleView from "../components/pos/SettleView";
import MoveTableDialog from "../components/MoveTableDialog";
import ModifierSheet from "../components/pos/ModifierSheet";
import { CashTenderDialog, SplitPaymentDialog } from "../components/pos/SettleDialogs";

const CATEGORIES = [
  { id: "drinks", label: "Drinks", icon: Coffee },
  { id: "food", label: "Food", icon: Utensils },
  { id: "shisha", label: "Shisha", icon: Flame },
  { id: "activities", label: "Play", icon: Gamepad2 },
];

// Stable random key for cart lines so two identical items with different
// modifier picks stay separate rows (e.g. two Macallan 12 with different
// ice prefs).
const cartLineKey = () =>
  `cl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// FastAPI / our endpoints sometimes return `detail` as an object instead
// of a string — notably the tab-top-up-required 402 payload
// (`{code, message, tab_id, current_total, paid_amount, unpaid_balance,
// projected_order_total, unpaid_limit, topup_minimum}`). Passing that
// object straight into `toast.error(...)` crashed React with
// "Objects are not valid as a React child". Pull `.message` out, fall
// back to the JSON body, and finally to a generic string.
const errMsg = (err, fallback = "Something went wrong") => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    // Tab-top-up payload: produce a clear staff-facing line.
    if (detail.code === "tab_topup_required") {
      const owed = Number(detail.unpaid_balance || 0).toFixed(2);
      const limit = Number(detail.unpaid_limit || 250).toFixed(0);
      return `Tab limit hit · $${owed} unpaid (cap $${limit}). Ask the customer to pay before adding more.`;
    }
    if (detail.code === "table_limit_exceeded") {
      const owed = Number(detail.unpaid_balance || 0).toFixed(2);
      const limit = Number(detail.table_unpaid_limit || 1000).toFixed(0);
      return `Table limit hit · $${owed} unpaid across this table (cap $${limit}). Settle a tab before adding more.`;
    }
    if (detail.message) return String(detail.message);
    try { return JSON.stringify(detail); } catch { /* noop */ }
  }
  return err?.message || fallback;
};

/* -----------------------------------------------------------
   ItemTile — large, dense, touch-friendly. No images by
   default (faster scan); shows thumb if menu item has one.
   ----------------------------------------------------------- */
function ItemTile({ item, onTap, disabled }) {
  const hasModifiers =
    (item.variants && item.variants.length > 0) ||
    (item.head_options && item.head_options.length > 0) ||
    (item.addon_groups && item.addon_groups.length > 0) ||
    item.is_custom_mix ||
    item.is_custom_charge ||
    item.category === "food"; // spice/notes prompt

  const minPrice =
    item.variants && item.variants.length > 0
      ? Math.min(...item.variants.map((v) => v.price || 0))
      : item.price;

  return (
    <button
      type="button"
      onClick={() => onTap(item)}
      disabled={disabled}
      data-testid={`pos-tile-${item.id}`}
      className={`group relative flex flex-col justify-between rounded-md border border-white/10 bg-zinc-900/60 hover:border-gold/60 hover:bg-zinc-800/80 active:scale-[0.98] transition text-left p-3 min-h-[110px] ${
        disabled ? "opacity-40 pointer-events-none" : ""
      }`}
    >
      {item.image_url ? (
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.18] rounded-md"
          style={{ backgroundImage: `url(${item.image_url})` }}
        />
      ) : null}
      <div className="relative z-10 flex-1">
        <p className="font-serif text-white text-[13px] leading-tight line-clamp-3">
          {item.name}
        </p>
        {item.subcategory && (
          <p className="mt-1 text-[9px] uppercase tracking-[0.14em] text-zinc-500">
            {item.subcategory}
          </p>
        )}
      </div>
      <div className="relative z-10 flex items-end justify-between mt-2">
        <span className="text-gold font-mono text-xs">
          {item.variants && item.variants.length > 0
            ? `from ${money(minPrice)}`
            : money(item.price)}
        </span>
        {hasModifiers && (
          <span className="text-[9px] uppercase tracking-[0.12em] text-zinc-500 font-sans">
            options
          </span>
        )}
      </div>
    </button>
  );
}


/* -----------------------------------------------------------
   ModifierSheet — extracted to /components/pos/ModifierSheet.jsx
   so POSPage.jsx stays focused on the till's overall flow.
   Imported at the top of this file.
   ----------------------------------------------------------- */

/* -----------------------------------------------------------
   CashTenderDialog + SplitPaymentDialog — extracted to
   /components/pos/SettleDialogs.jsx (Feb 2026). Imported at
   the top of this file.
   ----------------------------------------------------------- */
/* -----------------------------------------------------------
   FloorView — the per-table tile grid that powers the Till's
   landing screen lives in /components/TableFloorView so the
   serving panel can share the exact same tiles + colour code
   (Feb 2026 owner ask: "make the floor view on the serving
   panel be like that of the floor view on the till").
   ----------------------------------------------------------- */

/* -----------------------------------------------------------
   SettleView — extracted to /components/pos/SettleView.jsx so
   POSPage.jsx stays focused on the order-entry side of the till.
   Imported at the top of this file.
   ----------------------------------------------------------- */

/* -----------------------------------------------------------
   Main POS page
   ----------------------------------------------------------- */
export default function POSPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [menu, setMenu] = useState([]);
  const [tables, setTables] = useState([]);
  // Owner-curated section→subcategory map for the Drinks nav. Pulled from
  // `/api/menu/drinks-sections` so this stays in sync with the customer
  // menu — operator never has to update two places. e.g.
  //   { section: "Spirits",
  //     subcategories: ["Whisky","Vodka","Gin","Rum","Tequila","Cognac","Other Spirits"] }
  const [drinkSections, setDrinkSections] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(true);

  // tableId / customerName are kept around — they're set automatically
  // when the till supervisor clicks a tile in the floor view, NOT from
  // a dropdown in the header. Counter mode was removed entirely (Feb
  // 2026 owner directive: every sale rides a tab).
  const [tableId, setTableId] = useState("");
  const [customerName, setCustomerName] = useState("");
  // Party advance — owner ask Feb 2026. Till supervisor can capture an
  // upfront cash / EFTPOS amount when opening a new tab. We default to
  // empty (most orders aren't advance-funded), validated at submit.
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [advanceMethod, setAdvanceMethod] = useState("cash"); // "cash" | "eftpos"
  // Owner ask Feb 2026: when the till hits "Add Items" on an existing
  // tab from the Settle view, we skip the open-tab dedup call and just
  // POST a new order with this tab_id attached. Set by the Settle view's
  // Add Items handler; cleared on send / back.
  const [attachedTab, setAttachedTab] = useState(null); // {id, customer_name, guest_id}

  const [activeCat, setActiveCat] = useState("drinks");
  // For drinks → holds the section name ("Spirits"); for other categories →
  // a raw subcategory string ("Burgers"). Empty string = All.
  const [activeSub, setActiveSub] = useState("");
  const [search, setSearch] = useState("");

  const [cart, setCart] = useState([]); // [{_key, item_id, name, quantity, unit_price, ...}]
  // ---- view state machine — owner ask Feb 2026 ----
  // POS is now a floor-first console. 'floor' = grid of all dining-room
  // tables (green = available, red = occupied with customer name(s));
  // tapping an available tile drops into 'order' (table-bound item
  // picker, "Place Order & Start Tab" → returns to floor); tapping an
  // occupied tile drops into 'settle' (per-tab list with Settle Cash /
  // EFTPOS controls). Counter mode is gone — every sale rides a tab.
  const [view, setView] = useState("floor"); // 'floor' | 'order' | 'settle'
  // Ref to the customer-name input so we can focus + select the suggested
  // "Guest N" placeholder when staff hits "+ New Tab" — busy-night
  // friction kill (one tap to start, one keypress to overwrite).
  const customerNameRef = useRef(null);
  const [floorTables, setFloorTables] = useState([]);
  const [floorLoading, setFloorLoading] = useState(true);
  // The table the till supervisor selected from the floor — drives both
  // the order view (header chip, target table_id on send) and the
  // settle view (which tabs to list).
  const [activeFloorTable, setActiveFloorTable] = useState(null);
  // Move-table dialog state (Feb 2026 owner ask). Holds the tab being
  // relocated when MoveTableDialog is open.
  const [moveTab, setMoveTab] = useState(null);

  // "Sync Card Payments" — manually triggers the Square reconciler.
  // Feb 2026 owner ask: customers sometimes pay via Square and the till
  // doesn't see it (webhook didn't land). This button fires the same
  // self-heal sweep that runs every 30s, on demand. Manager / director
  // / bartender (the backend endpoint enforces it).
  const [syncing, setSyncing] = useState(false);
  const syncCardPayments = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/payments/reconcile");
      const fixed = data?.fixed ?? 0;
      const checked = data?.checked ?? 0;
      toast.success(
        fixed > 0
          ? `Synced · ${fixed} card payment${fixed === 1 ? "" : "s"} pulled in (checked ${checked})`
          : `Synced · nothing pending (checked ${checked})`,
        { duration: 5000 }
      );
      refreshFloor();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };
  const [settleBusy, setSettleBusy] = useState("");

  const [modifierItem, setModifierItem] = useState(null);
  // Custom-mix shisha gets its own dedicated dialog with flavour ratio
  // sliders — mirrors the customer flow so till + customer pick the same
  // way (no surprises at the bar).
  const [mixItem, setMixItem] = useState(null);
  // When non-null, a CashTenderDialog is shown for a settle. Shape:
  //   { kind: 'single', tab, total }    — settle one tab in cash
  //   { kind: 'all',   tabs, total }    — settle every tab on the table in cash
  // Confirming the dialog runs the same settle flow the EFTPOS path uses
  // and stamps the tender/change on the backend so analytics roll up.
  const [cashSettleTarget, setCashSettleTarget] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  // ---- floor data refresh ----
  // Pulls the per-table aggregate (`/tables/overview`) which already
  // bundles tabs per table — single call, no N+1. We poll every 8s so
  // the floor reflects guest-app and BarPanel changes without a manual
  // refresh.
  const refreshFloor = useCallback(async () => {
    try {
      const { data } = await api.get("/tables/overview");
      const rows = (Array.isArray(data) ? data : []).filter(
        (t) => !t.is_pos_counter
      );
      // Sort by table number ascending; non-numeric (e.g. "Counter")
      // fall to the end.
      rows.sort((a, b) => {
        const na = parseInt(String(a.number).replace(/\D/g, ""), 10) || 9999;
        const nb = parseInt(String(b.number).replace(/\D/g, ""), 10) || 9999;
        return na - nb;
      });
      setFloorTables(rows);
    } catch (e) {
      toast.error(`Floor refresh failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setFloorLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshFloor();
    const id = setInterval(refreshFloor, 8000);
    return () => clearInterval(id);
  }, [refreshFloor]);

  // ---- bootstrap: menu + drinks sections (table data comes from refreshFloor) ----
  useEffect(() => {
    let cancelled = false;
    setLoadingMenu(true);
    Promise.all([
      api.get("/menu/all").catch(() => api.get("/menu")), // fall back if not allowed
      api.get("/tables"),
      api.get("/menu/drinks-sections").catch(() => ({ data: [] })),
    ])
      .then(([m, t, ds]) => {
        if (cancelled) return;
        const items = Array.isArray(m.data) ? m.data : [];
        setMenu(items.filter((i) => i.is_available !== false));
        // `tables` state kept around for the order-view table chip; the
        // floor view uses `floorTables` which carries tab-aggregate info.
        const realTables = (t.data || []).filter((tb) => !tb.is_pos_counter);
        setTables(realTables);
        setDrinkSections(Array.isArray(ds.data) ? ds.data : []);
      })
      .catch((e) => {
        if (!cancelled) toast.error(`Failed to load: ${e.response?.data?.detail || e.message}`);
      })
      .finally(() => !cancelled && setLoadingMenu(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- derived: subcategory chips per active category ----
  // Drinks → owner-curated sections (Cocktails / Spirits / Beer & Wine / …)
  // Other categories → raw subcategories from the menu items.
  const subcategoryChips = useMemo(() => {
    if (activeCat === "drinks") {
      return drinkSections.map((s) => s.section);
    }
    const set = new Set();
    for (const m of menu) {
      if (m.category === activeCat && m.subcategory) set.add(m.subcategory);
    }
    return Array.from(set).sort();
  }, [menu, activeCat, drinkSections]);

  // Reset subcategory when category changes
  useEffect(() => {
    setActiveSub("");
  }, [activeCat]);

  // ---- derived: filtered items ----
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    // For drinks, the active sub is a SECTION name — translate to the set
    // of underlying leaf subcategories before filtering.
    let allowedSubs = null;
    if (activeCat === "drinks" && activeSub) {
      const sec = drinkSections.find((s) => s.section === activeSub);
      allowedSubs = sec ? new Set(sec.subcategories || []) : new Set();
    } else if (activeSub) {
      allowedSubs = new Set([activeSub]);
    }
    return menu.filter((m) => {
      if (m.category !== activeCat) return false;
      if (allowedSubs && !allowedSubs.has(m.subcategory)) return false;
      if (q && !(m.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [menu, activeCat, activeSub, search, drinkSections]);

  // ---- cart helpers ----
  const handleTileTap = (item) => {
    // Custom-mix shisha gets its own builder (flavour slots + sliders).
    if (item.is_custom_mix) {
      setMixItem(item);
      return;
    }
    // Fast path: no modifiers, no required choices → instant +1 to cart.
    const hasModifiers =
      (item.variants && item.variants.length > 0) ||
      (item.head_options && item.head_options.length > 0) ||
      (item.addon_groups && item.addon_groups.length > 0) ||
      (item.add_ons && item.add_ons.length > 0) ||
      item.is_custom_charge ||
      item.category === "food";
    if (hasModifiers) {
      setModifierItem(item);
      return;
    }
    // Merge with an existing identical line (same item_id, no modifiers)
    setCart((prev) => {
      const idx = prev.findIndex(
        (c) =>
          c.item_id === item.id &&
          !c.variant_label &&
          !c.head_label &&
          !(c.add_ons && c.add_ons.length) &&
          !c.spice_level &&
          !c.instructions
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [
        ...prev,
        {
          _key: cartLineKey(),
          item_id: item.id,
          name: item.name,
          category: item.category,
          quantity: 1,
          unit_price: item.price,
          variant_label: null,
          head_label: null,
          add_ons: [],
          spice_level: null,
          instructions: null,
          modifier_label: null,
        },
      ];
    });
  };

  // Callback that matches the existing `CustomMixDialog` onAdd contract.
  const addMixToCart = ({ mix_flavours, head_label, head_price, addOns, displayName, unitPrice }) => {
    if (!mixItem) return;
    setCart((prev) => [
      ...prev,
      {
        _key: cartLineKey(),
        item_id: mixItem.id,
        name: displayName,
        category: "shisha",
        quantity: 1,
        unit_price: unitPrice,
        variant_label: null,
        head_label: head_label || null,
        add_ons: (addOns || []).map((a) => ({
          name: a.name,
          price: Number(a.price) || 0,
        })),
        spice_level: null,
        instructions: null,
        mix_flavours: mix_flavours || null,
        modifier_label: [head_label, (addOns || []).map((a) => a.name).join(", ")]
          .filter(Boolean)
          .join(" · ") || null,
      },
    ]);
    setMixItem(null);
  };

  const updateQty = (key, delta) => {
    setCart((prev) =>
      prev
        .map((c) => (c._key === key ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c))
        .filter((c) => c.quantity > 0)
    );
  };

  const removeLine = (key) => setCart((prev) => prev.filter((c) => c._key !== key));
  const clearCart = () => setCart([]);

  const subtotal = useMemo(
    () => cart.reduce((s, c) => s + c.unit_price * c.quantity, 0),
    [cart]
  );

  // effectiveTableId is just the tableId now — set by the floor-tile
  // click (no Counter mode anymore).
  const effectiveTableId = tableId;

  // ---- pay flow ----
  // Owner spec Feb 2026: every till sale rides a tab — same as the
  // customer flow. The "Place Order & Start Tab" button posts to
  // `/api/orders` with payment_method='tab'. If the customer doesn't
  // have an open tab yet, the backend opens one automatically (no
  // deposit). Cash / EFTPOS / Apple Pay are decided ONLY at settle
  // time from the Settle view of the floor.
  const send = async () => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    if (!effectiveTableId) {
      toast.error("Pick a table from the floor first");
      return;
    }
    const name = (customerName || "").trim();
    if (!name) {
      toast.error("Enter the customer's name first");
      return;
    }

    setSubmitting(true);
    try {
      // Stable per (table, customer_name) guest_id so re-opening yields
      // the same tab — the open-tab endpoint dedups on (table_id +
      // customer_name_normalised) and rejects if the guest_id differs.
      // Hashing keeps the value compact + URL-safe; collisions across
      // different names on the same table are effectively impossible
      // because the input includes the table id.
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 24);
      const stableGuestId = `pos_${effectiveTableId.slice(0, 8)}_${slug || "guest"}`;

      // Idempotently open/find the tab. No deposit required (zero
      // upfront_amount, cash method) — matches the Feb 2026 customer
      // workflow exactly.
      // SHORT-CIRCUIT (Feb 2026): when staff clicked "Add Items" on an
      // existing tab from the Settle view, we already KNOW the tab id +
      // guest_id. Skip the open-tab dedup roundtrip and post the order
      // straight against that tab — prevents the (rare) 409 race when a
      // customer also has the QR open on the same tab.
      let tabId = "";
      let effectiveGuestId = stableGuestId;
      if (attachedTab?.id) {
        tabId = attachedTab.id;
        effectiveGuestId = attachedTab.guest_id || stableGuestId;
      } else {
      try {
        const { data: tab } = await api.post("/payments/open-tab", {
          table_id: effectiveTableId,
          customer_name: name,
          guest_id: stableGuestId,
          upfront_method: "cash",
          upfront_amount: 0,
          // Party advance (Feb 2026) — only sent when staff filled the
          // input. Backend wraps it into paid_amount + a payment_tx row
          // of kind=tab_advance, so the unpaid ceiling math accounts
          // for it from order #1 and the settle change-due maths it
          // automatically at the end.
          ...(Number(advanceAmount) > 0
            ? {
                advance_amount: Number(advanceAmount),
                advance_method: advanceMethod || "cash",
              }
            : {}),
          origin_url:
            typeof window !== "undefined" && window.location
              ? window.location.origin
              : "",
        });
        tabId = tab?.id || "";
      } catch (err) {
        // 409 = a tab is already open under this name on this table but
        // with a different guest_id (e.g. customer also QR-ordered). We
        // can't safely re-use it without their guest_id, so the cleanest
        // path is to ask the staff to rename — surface the server's own
        // suggestion verbatim. Other errors → re-throw.
        if (err.response?.status === 409) {
          toast.error(err.response.data?.detail || "Pick a different name");
          setSubmitting(false);
          return;
        }
        throw err;
      }
      }  // end of "no attached tab" branch

      const items = cart.map((c) => ({
        item_id: c.item_id,
        quantity: c.quantity,
        variant_label: c.variant_label || null,
        head_label: c.head_label || null,
        add_ons: c.add_ons || [],
        spice_level: c.spice_level || null,
        instructions: c.instructions || null,
        ...(c.mix_flavours ? { mix_flavours: c.mix_flavours } : {}),
        ...(c.custom_name
          ? {
              custom_name: c.custom_name,
              custom_price: c.custom_price,
              custom_notes: c.custom_notes,
            }
          : {}),
      }));

      await api.post("/orders", {
        table_id: effectiveTableId,
        items,
        payment_method: "tab",
        customer_name: name,
        guest_id: effectiveGuestId,
        tab_id: tabId || undefined,
      });

      toast.success(attachedTab ? `Items added to ${name}'s tab · ${money(subtotal)}` : `Tab updated · ${money(subtotal)}`);
      setCart([]);
      setCustomerName("");
      setAdvanceAmount("");
      setAdvanceMethod("cash");
      setAttachedTab(null);
      setActiveFloorTable(null);
      setTableId("");
      setView("floor");
      refreshFloor();
    } catch (e) {
      toast.error(errMsg(e, "Order failed"), { duration: 7000 });
    } finally {
      setSubmitting(false);
    }
  };

  // ---- floor → order: clicking an Available tile ----
  const openOrderForTable = (t) => {
    setActiveFloorTable(t);
    setTableId(t.id);
    setCustomerName("");
    setAdvanceAmount("");
    setAdvanceMethod("cash");
    setCart([]);
    setView("order");
  };

  // ---- floor → settle: clicking an Occupied tile ----
  const openSettleForTable = (t) => {
    setActiveFloorTable(t);
    setView("settle");
  };

  // ---- settle → order: clicking "Add Items" on an existing tab card ----
  // Owner ask Feb 2026: at the till, when a customer at an active tab
  // asks for "another Mojito", staff need a way to add it to THAT tab
  // (not start a new tab). Pre-fills the order view with the existing
  // tab's name + guest_id; placeOrder() detects `attachedTab` and skips
  // the open-tab dedup, posting straight against the existing tab.
  const addItemsToExistingTab = (tab) => {
    if (!activeFloorTable || !tab) return;
    setTableId(activeFloorTable.id);
    setCustomerName(tab.customer_name || "");
    setAdvanceAmount("");
    setAdvanceMethod("cash");
    setCart([]);
    setAttachedTab({
      id: tab.id,
      customer_name: tab.customer_name || "",
      guest_id: tab.guest_id || "",
    });
    setView("order");
  };

  // ---- settle → order: "Start a New Tab on this Table" ----
  // Owner ask Feb 2026: "till panel should be able to start more tabs
  // for same table. Because some times 2 friends order on table
  // separately." The backend already supports co-existing tabs (the
  // dedup is keyed by name + guest_id, not table). This button just
  // unblocks the UI path — it leaves activeFloorTable pinned so the
  // new order lands on this exact table, blanks the customer name so
  // staff types the new person's name, and CLEARS `attachedTab` so
  // placeOrder() runs the normal open-tab dedup and gets a fresh tab id.
  //
  // Follow-up (Feb 2026): pre-fill the name as "Guest N" (incrementing
  // per table) so staff only need to retype if the customer wants their
  // real name on the bill. Friction-reducing one-tap path on busy
  // nights. N is computed from the active tabs already on the table +
  // 1, so the suggested label never collides with an existing tab.
  const startNewTabOnTable = () => {
    if (!activeFloorTable) return;
    const existing = (activeFloorTable.active_tabs || activeFloorTable.tabs || []).length;
    const suggested = `Guest ${existing + 1}`;
    setTableId(activeFloorTable.id);
    setAttachedTab(null);
    setCustomerName(suggested);
    setAdvanceAmount("");
    setAdvanceMethod("cash");
    setCart([]);
    setView("order");
    // Focus + select the suggested name on the next frame so any
    // keystroke from the till operator replaces "Guest N" instantly.
    requestAnimationFrame(() => {
      if (customerNameRef.current) {
        customerNameRef.current.focus();
        customerNameRef.current.select();
      }
    });
  };

  // Per-tab order roll-up for the Settle view — fetched lazily when staff
  // opens a table and re-polled every 8s so live status updates as the
  // kitchen/bar marks items preparing → served. Keyed by tab_id.
  const [tabOrders, setTabOrders] = useState({}); // {tab_id: [orders]}
  // Server-stamped per-table unpaid ceiling, surfaced on the active
  // orders payload. Defaults to $1000 if the server hasn't sent one yet
  // — kept in sync with the backend `TABLE_LIMIT` constant.
  const [tableLimit, setTableLimit] = useState(1000);
  useEffect(() => {
    if (view !== "settle" || !activeFloorTable) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/tables/${activeFloorTable.id}/active-orders`);
        if (cancelled) return;
        const next = {};
        (data?.tabs || []).forEach((tab) => {
          next[tab.id] = tab.orders || [];
        });
        setTabOrders(next);
        if (data?.table_limit) setTableLimit(Number(data.table_limit));
      } catch (e) {
        // Silent — the floor poll already surfaces table-level errors.
      }
    };
    load();
    const id = setInterval(load, 8000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [view, activeFloorTable]);

  const backToFloor = () => {
    setActiveFloorTable(null);
    setTableId("");
    setCart([]);
    setAttachedTab(null);
    setView("floor");
    setTabOrders({});
    refreshFloor();
  };

  // ---- settle a single tab from the Settle view ----
  // Cash / EFTPOS both flow through the existing `/tabs/by-order/{id}/settle`
  // endpoint. We pull the tab's receipt to get any order_id on it (the
  // endpoint signature takes an order_id and closes the whole tab) — same
  // strategy BarPanel.settleCash uses, so behaviour is consistent across
  // surfaces.
  // Owner ask Feb 2026: split-payment target. When set, opens the
  // SplitPaymentDialog. Same shape as cashSettleTarget — kind +
  // tab/tabs + total + method always "split".
  const [splitSettleTarget, setSplitSettleTarget] = useState(null);
  const settleTab = async (tab, method) => {
    if (settleBusy) return;
    const owed = Math.max(
      0,
      Number(tab.current_total || 0) - Number(tab.paid_amount || 0)
    );
    const refund = Math.max(
      0,
      Number(tab.paid_amount || 0) - Number(tab.current_total || 0)
    );
    if (method === "split") {
      setSplitSettleTarget({ kind: "single", tab, total: owed });
      return;
    }
    setCashSettleTarget({ kind: "single", tab, total: owed, refund, method });
    return;
  };

  // Internal helper — POSTs the actual settle for one tab. Pulled out of
  // settleTab so cashSettleConfirm + settleAllTabs can share the exact
  // same code path. Optional cashReceived stamps tender + change on the
  // backend's payment_transaction.
  const _runTabSettle = async (tab, method, cashReceived, keepOnTab = false, discountPct = 0, managerPin = "", keepSeated = false) => {
    const { data: receipt } = await api.get(`/tabs/${tab.id}/receipt`);
    const orderId = receipt?.orders?.[0]?.id;
    if (!orderId) throw new Error("No order found on this tab");
    // Build body — `keep_change_as_credit` only flips the backend's
    // settle branch when a cash over-tender was actually supplied;
    // otherwise it's ignored server-side.
    const body = {};
    if (cashReceived != null) body.cash_received = cashReceived;
    if (keepOnTab) body.keep_change_as_credit = true;
    if (keepSeated) body.keep_seated = true;
    if (discountPct > 0) body.discount_pct = discountPct;
    if (managerPin) body.manager_pin = managerPin;
    const { data } = await api.post(
      `/tabs/by-order/${orderId}/settle?settlement_method=${method}`,
      body
    );
    return data;
  };

  // Internal helper — after a settle (single or all), refresh the floor
  // and drop back to floor view if the table has no more active tabs.
  const _refreshAfterSettle = async () => {
    await refreshFloor();
    const updated = (await api.get("/tables/overview")).data || [];
    const here = updated.find((x) => x.id === activeFloorTable?.id);
    const stillTabs = here ? (here.active_tabs || here.tabs || []) : [];
    if (!here || stillTabs.length === 0) {
      backToFloor();
    } else {
      setActiveFloorTable(here);
    }
  };

  // ---- settle ALL active tabs on the current table in one go ----
  // Owner ask Feb 2026: one customer often picks up the whole table —
  // staff need a single "Settle ALL" press that closes every active tab
  // with the same method. We loop through the existing per-tab settle
  // endpoint sequentially (NOT parallel — keeps the audit log tidy and
  // the table-cleaning state machine sane). Each tab's receipt fetch +
  // settle is its own request; if one fails the loop stops and the
  // remaining tabs stay open so staff can recover individually.
  const settleAllTabs = async (method) => {
    if (settleBusy || !activeFloorTable) return;
    const tabs = activeFloorTable.active_tabs || activeFloorTable.tabs || [];
    if (tabs.length === 0) {
      toast.error("No active tabs to settle");
      return;
    }
    // Compute net (sum_owed - sum_credit) so the dialog/confirm shows
    // the actual amount the customer pays after intra-table credits.
    const sumOwed = tabs.reduce(
      (s, t) => s + Math.max(0, Number(t.current_total || 0) - Number(t.paid_amount || 0)),
      0
    );
    const sumCredit = tabs.reduce(
      (s, t) => s + Math.max(0, Number(t.paid_amount || 0) - Number(t.current_total || 0)),
      0
    );
    const net = Math.max(0, sumOwed - sumCredit);

    if (method === "split") {
      setSplitSettleTarget({ kind: "all", tabs, total: net, sumOwed, sumCredit });
      return;
    }
    // Both cash and eftpos go through the same dialog now so staff can
    // pick a 10% / 20% discount + supply manager PIN where required.
    setCashSettleTarget({ kind: "all", tabs, total: net, sumOwed, sumCredit, method });
    return;
  };

  // Internal helper — atomic whole-table settle. Owner ask Feb 2026
  // (rewrite): the previous loop-based approach settled each tab in
  // sequence and the customer had to do credit math in their head. The
  // new endpoint `POST /api/tables/{id}/settle-all` nets credits across
  // every active tab on the table and charges the NET. Returns
  // `{net_owed, sum_owed, sum_credit, change_due, credit_kept_on_tab,
  // tabs_closed, tabs_kept_open, ...}`.
  const _runSettleAllAtomic = async ({ method, cashReceived, keepOnTab, discountPct = 0, managerPin = "", keepSeated = false }) => {
    if (!activeFloorTable) throw new Error("No active table");
    const body = { settlement_method: method };
    if (cashReceived != null) body.cash_received = cashReceived;
    if (keepOnTab) body.keep_change_as_credit = true;
    if (keepSeated) body.keep_seated = true;
    if (discountPct > 0) body.discount_pct = discountPct;
    if (managerPin) body.manager_pin = managerPin;
    const { data } = await api.post(
      `/tables/${activeFloorTable.id}/settle-all`,
      body
    );
    return data;
  };

  // ---- cash tender dialog confirm — drives the actual settle ----
  // Triggered when staff hits CONFIRM in CashTenderDialog. Knows which
  // mode it's in via the cashSettleTarget shape, runs the correct
  // settle path, and toasts the change due so the staff member can
  // count it out while the dialog closes.
  // Split-payment confirm — wires the dialog's payload through the
  // backend's existing settle endpoint (per-tab or settle-all) with the
  // new split_payment body fields.
  const onSplitSettleConfirm = async (opts) => {
    const target = splitSettleTarget;
    if (!target) return;
    setSplitSettleTarget(null);
    // ---- partial-pay branch (Feb 2026 rev) ----
    // Owner ask: when guests are still at the table the split sum can
    // be LESS than the bill (rest stays owed) or MORE (rest becomes
    // tab credit). We post EACH non-zero leg through the existing
    // topup endpoint so the tab stays open — same plumbing the
    // single-method short-pay flow uses. Discount is NOT applied here
    // because partial pays are part-collections, not final settlements.
    if (opts.partialPay && target.kind === "single") {
      setSettleBusy(target.tab.id);
      try {
        if (opts.splitCashAmount > 0) {
          const body = { amount: opts.splitCashAmount, method: "cash", kind: "topup" };
          if (opts.cashReceived != null) body.cash_received = opts.cashReceived;
          await api.post(`/tabs/${target.tab.id}/confirm-payment`, body);
        }
        if (opts.splitEftposAmount > 0) {
          await api.post(`/tabs/${target.tab.id}/confirm-payment`, {
            amount: opts.splitEftposAmount,
            method: "eftpos",
            kind: "topup",
          });
        }
        const takenSum = opts.splitCashAmount + opts.splitEftposAmount;
        toast.success(
          `Partial split · Cash $${opts.splitCashAmount.toFixed(2)} + EFTPOS $${opts.splitEftposAmount.toFixed(2)} = $${takenSum.toFixed(2)} · tab stays open`,
          { duration: 9000 }
        );
        await _refreshAfterSettle();
      } catch (err) {
        toast.error(errMsg(err, "Partial split failed"), { duration: 5000 });
      } finally {
        setSettleBusy("");
      }
      return;
    }
    const body = {
      split_payment: true,
      split_cash_amount: opts.splitCashAmount,
      split_eftpos_amount: opts.splitEftposAmount,
      // Whole-bill discount (0 / 10 / 20) applied BEFORE the split (Feb
      // 2026 rev). Replaces the older `split_cash_10_off` flag that
      // only discounted the cash side and confused staff.
      discount_pct: opts.splitDiscountPct || 0,
      manager_pin: opts.managerPin || "",
      cash_received: opts.cashReceived,
    };
    if (opts.keepSeated) body.keep_seated = true;

    if (target.kind === "single") {
      setSettleBusy(target.tab.id);
      try {
        const { data: receipt } = await api.get(`/tabs/${target.tab.id}/receipt`);
        const orderId = receipt?.orders?.[0]?.id;
        if (!orderId) throw new Error("No order found on this tab");
        const { data } = await api.post(`/tabs/by-order/${orderId}/settle`, body);
        const discTag = data.discount_amount > 0 ? ` · 10% off cash ($${data.discount_amount.toFixed(2)})` : "";
        toast.success(
          `Tab settled · Cash $${opts.splitCashAmount.toFixed(2)} + EFTPOS $${opts.splitEftposAmount.toFixed(2)}${discTag}`,
          { duration: 9000 }
        );
        await _refreshAfterSettle();
      } catch (err) {
        toast.error(errMsg(err, "Split settle failed"), { duration: 5000 });
      } finally {
        setSettleBusy("");
      }
      return;
    }
    // kind === "all"
    setSettleBusy("__all__");
    try {
      const { data } = await api.post(`/tables/${activeFloorTable.id}/settle-all`, {
        ...body,
        settlement_method: "split",
      });
      const discTag = data.discount_amount > 0 ? ` · 10% off cash` : "";
      toast.success(
        `Table closed · Cash $${opts.splitCashAmount.toFixed(2)} + EFTPOS $${opts.splitEftposAmount.toFixed(2)}${discTag}`,
        { duration: 9000 }
      );
      await _refreshAfterSettle();
    } catch (err) {
      toast.error(errMsg(err, "Split settle-all failed"), { duration: 5000 });
    } finally {
      setSettleBusy("");
    }
  };


  const onCashSettleConfirm = async (tenderedNum, { keepOnTab = false, keepSeated = false, discountPct = 0, managerPin = "", shortPay = false } = {}) => {
    const target = cashSettleTarget;
    if (!target) return;
    const method = target.method || "cash";
    const change = method === "cash" ? Math.max(0, tenderedNum - target.total) : 0;
    const keepingChange = keepOnTab && change > 0.001;
    setCashSettleTarget(null);
    // ---- short-pay branch (owner ask Feb 2026) ----
    // Customer pays part of the bill while the tab stays open and they
    // keep ordering. We route through the existing top-up endpoint
    // (`/tabs/{id}/confirm-payment` with kind=topup) regardless of cash
    // or EFTPOS — same plumbing the Top up · Cash / Top up · EFTPOS
    // buttons already use. The settle endpoint is NOT called so the tab
    // is NOT closed.
    if (shortPay && target.kind === "single") {
      setSettleBusy(target.tab.id);
      try {
        const body = {
          amount: tenderedNum,
          method,
          kind: "topup",
        };
        if (method === "cash") body.cash_received = tenderedNum;
        const { data } = await api.post(`/tabs/${target.tab.id}/confirm-payment`, body);
        toast.success(
          `Short pay accepted · ${method === "cash" ? "Cash" : "EFTPOS"} $${tenderedNum.toFixed(2)} · tab stays open`,
          { duration: 8000 }
        );
        await _refreshAfterSettle();
      } catch (err) {
        toast.error(errMsg(err, "Short pay failed"), { duration: 5000 });
      } finally {
        setSettleBusy("");
      }
      return;
    }
    if (target.kind === "single") {
      setSettleBusy(target.tab.id);
      try {
        // When the tab has surplus credit (paid > current_total), owed
        // is $0 and the customer doesn't tender anything — they receive
        // a refund. Don't send cash_received in that case so the backend
        // doesn't try to compute change_due against $0.
        const refundOnly = (target.refund || 0) > 0.005 && (target.total || 0) < 0.005;
        const result = await _runTabSettle(
          target.tab,
          method,
          (method === "cash" && !refundOnly) ? tenderedNum : null,
          keepingChange,
          discountPct,
          managerPin,
          keepSeated,
        );
        const refundFromServer = Number(result?.credit_refunded || 0);
        if (refundFromServer > 0.005) {
          toast.success(
            `Tab closed · Refund $${refundFromServer.toFixed(2)} to ${target.tab.customer_name || "customer"}`,
            { duration: 9000 }
          );
        } else if (result?.kept_as_credit) {
          toast.success(
            `$${change.toFixed(2)} kept on ${target.tab.customer_name || "tab"} · tab stays open`,
            { duration: 8000 }
          );
        } else if (method === "eftpos") {
          toast.success(
            `Tab settled · EFTPOS $${(result?.owed_at_counter ?? target.total).toFixed(2)}` +
              (discountPct > 0 ? ` · ${discountPct}% off` : ""),
            { duration: 8000 }
          );
        } else {
          toast.success(
            `Tab settled · Cash $${tenderedNum.toFixed(2)} · Change $${change.toFixed(2)}` +
              (discountPct > 0 ? ` · ${discountPct}% off` : ""),
            { duration: 8000 }
          );
        }
        await _refreshAfterSettle();
      } catch (err) {
        toast.error(
          errMsg(err, "Settle failed"),
          { duration: 5000 }
        );
      } finally {
        setSettleBusy("");
      }
      return;
    }
    // kind === "all" — atomic whole-table settle.
    setSettleBusy("__all__");
    try {
      const result = await _runSettleAllAtomic({
        method,
        cashReceived: method === "cash" ? tenderedNum : undefined,
        keepOnTab: keepingChange,
        discountPct,
        managerPin,
        keepSeated,
      });
      const closedCount = (result?.tabs_closed || []).length;
      if (result?.credit_kept_on_tab > 0) {
        toast.success(
          `$${result.credit_kept_on_tab.toFixed(2)} kept as credit · ${closedCount} closed · 1 stays open`,
          { duration: 9000 }
        );
      } else if (method === "eftpos") {
        const refundLine =
          result?.table_credit_refunded > 0
            ? ` · refund $${result.table_credit_refunded.toFixed(2)}`
            : "";
        toast.success(
          `Table closed · ${closedCount} tabs · EFTPOS $${result.net_owed.toFixed(2)}${refundLine}` +
            (discountPct > 0 ? ` · ${discountPct}% off` : ""),
          { duration: 9000 }
        );
      } else {
        const refundLine =
          result?.table_credit_refunded > 0
            ? ` · refund $${result.table_credit_refunded.toFixed(2)}`
            : "";
        toast.success(
          `Table closed · ${closedCount} tabs · Cash $${tenderedNum.toFixed(2)} · Change $${(result.change_due || 0).toFixed(2)}${refundLine}` +
            (discountPct > 0 ? ` · ${discountPct}% off` : ""),
          { duration: 9000 }
        );
      }
      await _refreshAfterSettle();
    } catch (err) {
      toast.error(
        errMsg(err, "Settle all failed"),
        { duration: 7000 }
      );
    } finally {
      setSettleBusy("");
    }
  };

  const handleLogout = useCallback(async () => {
    try {
      await logout?.();
    } finally {
      navigate("/login", { replace: true });
    }
  }, [logout, navigate]);

  return (
    <div className="h-screen bg-black text-white flex flex-col overflow-hidden">
      {/* HEADER — shared across all 3 views; left-side content adapts */}
      <header className="flex items-center gap-3 px-4 h-14 border-b border-white/10 bg-zinc-950">
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-gold" />
          <h1 className="font-serif text-lg tracking-wide">
            Majnu <span className="text-gold">POS</span>
          </h1>
        </div>

        {/* View-aware breadcrumbs */}
        {view === "floor" && (
          <div className="ml-3 flex items-center gap-2" data-testid="pos-view-floor-label">
            <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans">
              Floor
            </span>
            <button
              type="button"
              onClick={refreshFloor}
              className="text-zinc-500 hover:text-zinc-300 transition"
              title="Refresh"
              data-testid="pos-floor-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${floorLoading ? "animate-spin" : ""}`} />
            </button>
            {/* "Sync Card Payments" — Feb 2026 owner ask. Fires the
                Square reconciler POST so any missed-webhook tab top-ups
                land on the till without waiting for the next 30s tick.
                Manager / director / bartender only. */}
            <button
              type="button"
              onClick={syncCardPayments}
              disabled={syncing}
              className="ml-1 inline-flex items-center gap-1.5 h-7 px-2 rounded-sm border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 text-purple-200 text-[10px] uppercase tracking-[0.12em] font-sans transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="pos-sync-card-payments"
              title="Force-poll Square for any tab card payments the webhook hasn't delivered yet."
            >
              <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync Card"}
            </button>
          </div>
        )}

        {(view === "order" || view === "settle") && (
          <button
            type="button"
            onClick={backToFloor}
            className="ml-3 inline-flex items-center gap-1.5 h-9 px-2.5 rounded border border-white/10 hover:border-white/25 text-zinc-300 hover:text-white text-xs font-sans uppercase tracking-[0.12em] transition"
            data-testid="pos-back-to-floor"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Floor
          </button>
        )}

        {view === "order" && activeFloorTable && (
          <div className="ml-2 inline-flex items-center gap-2 h-9 px-3 rounded bg-gold/10 border border-gold/40 text-gold text-xs font-sans uppercase tracking-[0.12em]" data-testid="pos-order-table-chip">
            <Users className="w-3.5 h-3.5" />
            Table {activeFloorTable.number}
          </div>
        )}

        {/* Owner ask Feb 2026 — "Add Items to existing tab" mode banner.
            Shows the locked-in tab name so the till operator can't
            accidentally retype it (which would create a sibling tab). */}
        {view === "order" && attachedTab && (
          <div className="ml-2 inline-flex items-center gap-2 h-9 px-3 rounded bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs font-sans uppercase tracking-[0.12em]" data-testid="pos-add-items-banner">
            <Plus className="w-3.5 h-3.5" />
            Adding to · {attachedTab.customer_name || "Tab"}
          </div>
        )}

        {view === "settle" && activeFloorTable && (
          <div className="ml-2 inline-flex items-center gap-2 h-9 px-3 rounded bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs font-sans uppercase tracking-[0.12em]" data-testid="pos-settle-table-chip">
            <Receipt className="w-3.5 h-3.5" />
            Settle · Table {activeFloorTable.number}
          </div>
        )}

        {/* Customer name input — only in order view AND only when staff is
            opening a fresh tab (not adding items to an existing one).
            Locking it in attached-tab mode prevents the operator from
            accidentally retyping the name and forking a new tab. */}
        {view === "order" && !attachedTab && (
          <input
            ref={customerNameRef}
            type="text"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value.slice(0, 60))}
            onFocus={(e) => e.currentTarget.select()}
            placeholder="Customer name"
            className="h-9 bg-zinc-900 border border-white/10 rounded px-3 text-sm text-white w-56 ml-2"
            data-testid="pos-customer-name"
          />
        )}

        {/* Party advance (optional) — only in order view AND only when
            opening a NEW tab. Adding items to an existing tab uses the
            tab's existing paid_amount; advance fields don't apply. */}
        {view === "order" && !attachedTab && (
          <div className="ml-2 flex items-center gap-1.5" data-testid="pos-advance-block">
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={advanceAmount}
                onChange={(e) => setAdvanceAmount(e.target.value)}
                placeholder="Advance"
                title="Optional party advance — cash/EFTPOS taken at the door"
                className="h-9 bg-zinc-900 border border-white/10 rounded pl-5 pr-2 text-sm text-white w-24"
                data-testid="pos-advance-amount"
              />
            </div>
            <div className="flex items-center bg-zinc-900 border border-white/10 rounded h-9 overflow-hidden text-[10px] uppercase tracking-[0.12em]">
              {["cash", "eftpos"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setAdvanceMethod(m)}
                  className={`px-2 h-full transition-colors ${
                    advanceMethod === m
                      ? "bg-emerald-500/15 text-emerald-300"
                      : "text-zinc-500 hover:text-zinc-300"
                  }`}
                  data-testid={`pos-advance-method-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex-1" />
        <div className="text-right pr-3">
          <p className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-sans">
            On shift
          </p>
          <p className="text-xs text-zinc-300">{user?.name || user?.email}</p>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="h-9 px-3 border border-white/10 hover:border-white/30 rounded text-xs uppercase tracking-[0.12em] text-zinc-300 flex items-center gap-1.5"
          data-testid="pos-sign-out"
        >
          <LogOut className="w-3.5 h-3.5" />
          Sign out
        </button>
      </header>

      {/* BODY */}
      {view === "floor" && (
        <TableFloorView
          tables={floorTables}
          loading={floorLoading}
          onAvailable={openOrderForTable}
          onOccupied={openSettleForTable}
          onCleaning={openSettleForTable}
          testId="pos-floor-view"
        />
      )}

      {view === "settle" && activeFloorTable && (
        <SettleView
          table={activeFloorTable}
          tabOrders={tabOrders}
          tableLimit={tableLimit}
          busyId={settleBusy}
          onSettle={settleTab}
          onSettleAll={settleAllTabs}
          onBack={backToFloor}
          onAddItems={addItemsToExistingTab}
          onMove={(tab) => setMoveTab(tab)}
          onStartNewTab={startNewTabOnTable}
        />
      )}

      {/* Move-table dialog (Feb 2026) — relocates a tab + its active
          orders to a different table in one POST. Source frees if no
          other tabs remain on it. */}
      <MoveTableDialog
        open={!!moveTab}
        onOpenChange={(o) => { if (!o) setMoveTab(null); }}
        tab={moveTab}
        sourceTableNumber={activeFloorTable?.number}
        onMoved={async () => {
          setMoveTab(null);
          // Refresh floor + settle view so the moved tab shows on the
          // destination next tick. backToFloor() also re-fetches /tables.
          backToFloor();
        }}
      />

      {view === "order" && (
      <div className="flex flex-1 min-h-0">
        {/* LEFT — menu */}
        <section className="flex-1 flex flex-col min-w-0 border-r border-white/10">
          {/* Category tabs */}
          <div className="flex border-b border-white/10 bg-zinc-950" data-testid="pos-categories">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const active = activeCat === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveCat(c.id)}
                  className={`flex-1 h-12 flex items-center justify-center gap-2 text-sm font-sans uppercase tracking-[0.14em] transition ${
                    active
                      ? "text-gold border-b-2 border-gold bg-gold/[0.05]"
                      : "text-zinc-400 hover:text-white"
                  }`}
                  data-testid={`pos-cat-${c.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {c.label}
                </button>
              );
            })}
          </div>

          {/* Subcategory chips */}
          {subcategoryChips.length > 0 && (
            <div className="flex gap-1.5 px-3 py-2 overflow-x-auto border-b border-white/5 bg-zinc-950/60" data-testid="pos-subcategories">
              <button
                type="button"
                onClick={() => setActiveSub("")}
                className={`shrink-0 h-8 px-3 text-[11px] uppercase tracking-[0.12em] rounded-full border transition ${
                  !activeSub
                    ? "border-gold text-gold bg-gold/10"
                    : "border-white/10 text-zinc-400 hover:text-white"
                }`}
                data-testid="pos-sub-all"
              >
                All
              </button>
              {subcategoryChips.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setActiveSub(s)}
                  className={`shrink-0 h-8 px-3 text-[11px] uppercase tracking-[0.12em] rounded-full border transition ${
                    activeSub === s
                      ? "border-gold text-gold bg-gold/10"
                      : "border-white/10 text-zinc-400 hover:text-white"
                  }`}
                  data-testid={`pos-sub-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="px-3 py-2 border-b border-white/5 bg-zinc-950/40">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search menu…"
                className="w-full h-9 pl-9 pr-3 bg-zinc-900 border border-white/10 rounded text-sm text-white"
                data-testid="pos-search"
              />
            </div>
          </div>

          {/* Item grid */}
          <div className="flex-1 overflow-y-auto p-3" data-testid="pos-items">
            {loadingMenu ? (
              <div className="flex items-center justify-center h-40 text-zinc-500">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : filteredItems.length === 0 ? (
              <p className="text-center text-zinc-500 text-sm mt-10">No items match.</p>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2">
                {filteredItems.map((m) => (
                  <ItemTile key={m.id} item={m} onTap={handleTileTap} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* RIGHT — cart */}
        <aside className="w-[360px] flex flex-col bg-zinc-950" data-testid="pos-cart">
          <div className="flex items-center justify-between px-4 h-12 border-b border-white/10">
            <h2 className="font-serif text-base">
              Order
              {cart.length > 0 && (
                <span className="ml-2 text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-sans">
                  · {cart.reduce((n, c) => n + c.quantity, 0)} items
                </span>
              )}
            </h2>
            {cart.length > 0 && (
              <button
                type="button"
                onClick={clearCart}
                className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 hover:text-rose-400 flex items-center gap-1"
                data-testid="pos-cart-clear"
              >
                <Trash2 className="w-3 h-3" />
                Clear
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-2">
            {cart.length === 0 ? (
              <div className="text-center text-zinc-600 text-sm mt-16">
                <Receipt className="w-8 h-8 mx-auto opacity-40 mb-2" />
                Tap items to start the order.
              </div>
            ) : (
              <ul className="space-y-2">
                {cart.map((c) => (
                  <li
                    key={c._key}
                    className="rounded border border-white/5 bg-zinc-900/50 p-2"
                    data-testid={`pos-cart-line-${c._key}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white leading-tight line-clamp-2">{c.name}</p>
                        {c.modifier_label && (
                          <p className="text-[10px] text-zinc-500 mt-0.5">{c.modifier_label}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLine(c._key)}
                        className="text-zinc-600 hover:text-rose-400 p-0.5"
                        data-testid={`pos-cart-remove-${c._key}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <div className="flex items-center gap-1 bg-black/40 rounded">
                        <button
                          type="button"
                          onClick={() => updateQty(c._key, -1)}
                          className="w-7 h-7 grid place-items-center text-zinc-400 hover:text-white"
                          data-testid={`pos-cart-minus-${c._key}`}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-6 text-center text-xs font-mono">{c.quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateQty(c._key, +1)}
                          className="w-7 h-7 grid place-items-center text-zinc-400 hover:text-white"
                          data-testid={`pos-cart-plus-${c._key}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="text-gold font-mono text-sm">
                        {money(c.unit_price * c.quantity)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Totals + Payment */}
          <div className="border-t border-white/10 p-3 space-y-2 bg-zinc-950">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">Subtotal</span>
              <span className="font-mono text-white" data-testid="pos-subtotal">
                {money(subtotal)}
              </span>
            </div>
            <div className="flex items-center justify-between text-base pt-1 border-t border-white/5">
              <span className="text-zinc-300 font-sans uppercase tracking-[0.14em] text-xs">
                Total
              </span>
              <span className="font-mono text-gold text-lg" data-testid="pos-total">
                {money(subtotal)}
              </span>
            </div>

            <Button
              onClick={() => send()}
              disabled={submitting || cart.length === 0}
              className="w-full h-14 mt-2 bg-gold hover:bg-gold/90 text-black font-sans uppercase tracking-[0.14em] text-sm font-semibold disabled:opacity-40"
              data-testid="pos-place-order"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Place Order &amp; Start Tab
            </Button>
            <p className="text-[10px] text-zinc-500 font-sans text-center pt-1">
              Payment chosen at <span className="text-zinc-300">Settle Tab</span> when the customer is done
            </p>
          </div>
        </aside>
      </div>
      )}

      {/* Modifier sheet */}
      <ModifierSheet
        item={modifierItem}
        open={!!modifierItem}
        onClose={() => setModifierItem(null)}
        onAdd={(line) => setCart((prev) => [...prev, line])}
      />

      {/* Custom-mix shisha builder — mirrors the customer flow exactly so
          a regular building their mix at the bar gets the same dialog they
          see when scanning their QR. */}
      <CustomMixDialog
        open={!!mixItem}
        onOpenChange={(o) => !o && setMixItem(null)}
        item={mixItem}
        availableFlavours={menu
          .filter((m) => m.category === "shisha" && !m.is_custom_mix && m.is_available !== false)
          .map((m) => ({ id: m.id, name: m.name, subcategory: m.subcategory || "" }))}
        onAdd={addMixToCart}
      />

      {/* Cash tender — kept around for the bartender's Bar Panel cash
          path. The new POS workflow chooses payment at SETTLE time on
          the floor's Settle view, not at order-create time, so this
          dialog is currently inert on this surface. */}

      {/* Cash tender dialog — opens when staff taps Settle Cash or
          Top Up · Cash. Shows change due so the staff can count it
          out, then runs the appropriate endpoint and stamps tender +
          change on the audit log. The "Keep on tab" toggle (only
          surfaced on SETTLE calls, single-tab kind) converts an
          over-tender into a top-up: paid_amount += full tender, tab
          stays active for more orders. */}
      <SplitPaymentDialog
        open={!!splitSettleTarget}
        total={splitSettleTarget ? splitSettleTarget.total : 0}
        allowKeepSeated={splitSettleTarget?.kind === "single" || splitSettleTarget?.kind === "all"}
        title={splitSettleTarget?.kind === "all" ? "Split payment · All tabs" : undefined}
        onClose={() => setSplitSettleTarget(null)}
        onConfirm={onSplitSettleConfirm}
      />


      <CashTenderDialog
        open={!!cashSettleTarget}
        total={cashSettleTarget ? cashSettleTarget.total : 0}
        refundDue={cashSettleTarget ? (cashSettleTarget.refund || 0) : 0}
        title={
          cashSettleTarget?.kind === "all"
            ? `Settle all tabs · ${cashSettleTarget?.method === "eftpos" ? "EFTPOS" : "Cash"}`
            : `Settle tab · ${cashSettleTarget?.method === "eftpos" ? "EFTPOS" : "Cash"}`
        }
        // Cash mode allows keep-on-tab; EFTPOS mode (no change) doesn't.
        paymentMode={cashSettleTarget?.method === "eftpos" ? "eftpos" : "cash"}
        allowKeepOnTab={
          (cashSettleTarget?.method ?? "cash") === "cash" &&
          (cashSettleTarget?.kind === "single" || cashSettleTarget?.kind === "all")
        }
        // Settle / settle-all callers can apply a 10% / 20% discount.
        allowDiscount={cashSettleTarget?.kind === "single" || cashSettleTarget?.kind === "all"}
        // Owner ask Feb 2026: bill paid but guests still seated → table
        // skips the auto-flip to cleaning. Only relevant on settle paths.
        allowKeepSeated={cashSettleTarget?.kind === "single" || cashSettleTarget?.kind === "all"}
        // Owner ask Feb 2026: short payments (customer pays part of the
        // bill, tab stays open). Only offered on per-tab settle (cash or
        // EFTPOS); not on settle-all (we don't net partials across
        // multiple tabs).
        allowShortPayment={cashSettleTarget?.kind === "single"}
        onClose={() => setCashSettleTarget(null)}
        onConfirm={onCashSettleConfirm}
      />

      {submitting && (
        <div className="fixed inset-0 bg-black/60 grid place-items-center z-50">
          <div className="bg-zinc-900 border border-white/10 rounded-lg px-6 py-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-gold" />
            <span className="text-white text-sm">Sending order…</span>
          </div>
        </div>
      )}
    </div>
  );
}
